import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input'];
  static values = {
    url: String,
    context: { type: String, default: 'contractor' },
    existingRecordId: { type: Number, default: 0 },
    minLength: { type: Number, default: 3 },
    debounce: { type: Number, default: 500 },
    disabled: { type: Boolean, default: false },
  };

  connect() {
    this.timeout = null;
    this.abortController = null;
    this.selectedIndex = -1;
    this.dropdownId = `sirene-typeahead-dropdown-${Math.random().toString(36).slice(2, 9)}`;

    // Create dropdown container
    this.dropdown = document.createElement('div');
    this.dropdown.id = this.dropdownId;
    this.dropdown.setAttribute('role', 'listbox');
    this.dropdown.className =
      'hidden absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-60 overflow-y-auto';
    // Insert dropdown after the input's parent (the InputComponent wrapper)
    const inputWrapper = this.inputTarget.closest('.relative');
    if (inputWrapper) {
      inputWrapper.after(this.dropdown);
    } else {
      this.element.appendChild(this.dropdown);
    }

    // Set ARIA attributes on input
    this.inputTarget.setAttribute('role', 'combobox');
    this.inputTarget.setAttribute('aria-autocomplete', 'list');
    this.inputTarget.setAttribute('aria-expanded', 'false');
    this.inputTarget.setAttribute('aria-controls', this.dropdownId);

    // Close dropdown on outside click
    this.outsideClickHandler = (e) => {
      if (!this.element.contains(e.target)) {
        this.hideDropdown();
      }
    };
    document.addEventListener('click', this.outsideClickHandler);

    // Listen for sirene-lookup events (sibling controller communication via document)
    this.lookupResultsHandler = () => this.hideDropdown();
    this.lookupSelectedHandler = (e) => this.onSiretSelected(e);
    this.lookupClearedHandler = () => this.onSiretCleared();
    document.addEventListener('sirene-lookup:results-shown', this.lookupResultsHandler);
    document.addEventListener('sirene-lookup:selected', this.lookupSelectedHandler);
    document.addEventListener('sirene-lookup:cleared', this.lookupClearedHandler);
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout);
    if (this.abortController) this.abortController.abort();
    document.removeEventListener('click', this.outsideClickHandler);
    document.removeEventListener('sirene-lookup:results-shown', this.lookupResultsHandler);
    document.removeEventListener('sirene-lookup:selected', this.lookupSelectedHandler);
    document.removeEventListener('sirene-lookup:cleared', this.lookupClearedHandler);
    if (this.dropdown?.parentNode) {
      this.dropdown.parentNode.removeChild(this.dropdown);
    }
  }

  disabledValueChanged(isDisabled) {
    if (isDisabled) {
      this.hideDropdown();
    }
  }

  // ── Input handlers ──

  onInput() {
    if (this.disabledValue) return;

    const query = this.inputTarget.value.trim();

    if (query.length < this.minLengthValue) {
      this.hideDropdown();
      return;
    }

    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.performSearch(query);
    }, this.debounceValue);
  }

  onKeydown(event) {
    if (this.disabledValue) return;

    const items = this.dropdown.querySelectorAll('[data-result-index]');

    switch (event.key) {
      case 'ArrowDown':
        if (!items.length) return;
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
        this.updateHighlight(items);
        break;
      case 'ArrowUp':
        if (!items.length) return;
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.updateHighlight(items);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          items[this.selectedIndex].click();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.hideDropdown();
        this.inputTarget.blur();
        break;
    }
  }

  // ── Cross-controller event handlers ──

  onSiretSelected(event) {
    const result = event.detail;
    if (result && result.company_name) {
      this.inputTarget.value = result.company_name;
    }
  }

  onSiretCleared() {
    this.inputTarget.value = '';
  }

  // ── Search ──

  async performSearch(query) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.showLoading();

    try {
      const params = new URLSearchParams({
        q: query,
        context: this.contextValue,
      });
      if (this.existingRecordIdValue > 0) {
        params.set('exclude_id', this.existingRecordIdValue);
      }

      const response = await fetch(`${this.urlValue}?${params}`, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error('API error');

      const data = await response.json();

      if (data.error) {
        this.hideDropdown();
        return;
      }

      if (data.results && data.results.length > 0) {
        this.showResults(data.results);
      } else {
        this.hideDropdown();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.hideDropdown();
    }
  }

  // ── Display ──

  showLoading() {
    this.dropdown.textContent = '';

    const div = document.createElement('div');
    div.className = 'p-4 text-center text-sm text-neutral-500';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'inline-block h-4 w-4 text-primary-600 animate-spin mr-2');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('viewBox', '0 0 24 24');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-width', '2');
    path.setAttribute(
      'd',
      'M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99'
    );
    svg.appendChild(path);
    div.appendChild(svg);
    div.appendChild(document.createTextNode('Recherche en cours...'));
    this.dropdown.appendChild(div);

    this.dropdown.classList.remove('hidden');
    this.inputTarget.setAttribute('aria-expanded', 'true');

    // Mutual exclusivity: tell sirene-lookup to close its results
    this.dispatch('results-shown', { bubbles: true });
  }

  showResults(results) {
    this.selectedIndex = -1;
    this.dropdown.textContent = '';

    results.forEach((result, index) => {
      const formattedSiret = result.siret ? this.formatSiret(result.siret) : '';
      const location = [formattedSiret, result.postal_code, result.city]
        .filter(Boolean)
        .join(' \u00b7 ');

      const item = document.createElement('div');
      item.setAttribute('data-result-index', index);
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.className =
        'cursor-pointer p-3 hover:bg-primary-50 border-b border-neutral-100 last:border-0 transition-colors';

      // Store result data for click handler
      item.addEventListener('click', () => this.selectResult(result));

      const nameDiv = document.createElement('div');
      nameDiv.className = 'text-sm font-medium text-neutral-900';
      nameDiv.textContent = result.company_name || '';
      item.appendChild(nameDiv);

      const locationDiv = document.createElement('div');
      locationDiv.className = 'text-xs text-neutral-500';
      locationDiv.textContent = location;
      item.appendChild(locationDiv);

      this.dropdown.appendChild(item);
    });

    const footer = document.createElement('div');
    footer.className = 'px-3 py-2 text-xs text-neutral-400 border-t border-neutral-100';
    footer.textContent = `${results.length} résultat${results.length > 1 ? 's' : ''}`;
    this.dropdown.appendChild(footer);

    this.dropdown.classList.remove('hidden');
    this.inputTarget.setAttribute('aria-expanded', 'true');

    // Mutual exclusivity
    this.dispatch('results-shown', { bubbles: true });
  }

  selectResult(result) {
    this.inputTarget.value = result.company_name || '';
    this.hideDropdown();
    this.dispatch('selected', { detail: result, bubbles: true });
  }

  updateHighlight(items) {
    items.forEach((item, i) => {
      const isSelected = i === this.selectedIndex;
      item.classList.toggle('bg-primary-50', isSelected);
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  }

  hideDropdown() {
    this.dropdown.textContent = '';
    this.dropdown.classList.add('hidden');
    this.selectedIndex = -1;
    this.inputTarget.setAttribute('aria-expanded', 'false');
  }

  formatSiret(digits) {
    const d = digits.replace(/\D/g, '').substring(0, 14);
    const parts = [];
    if (d.length > 0) parts.push(d.substring(0, Math.min(3, d.length)));
    if (d.length > 3) parts.push(d.substring(3, Math.min(6, d.length)));
    if (d.length > 6) parts.push(d.substring(6, Math.min(9, d.length)));
    if (d.length > 9) parts.push(d.substring(9, Math.min(14, d.length)));
    return parts.join(' ');
  }
}
